import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { Epic, Requirement, TaskStatus } from '../types';

const STATUS_OPTIONS: TaskStatus[] = [
  'Not Started',
  'Active',
  'In Progress',
  'Code Complete',
  'In Release',
  'Completed',
  'Blocked',
  'Paused',
  'Deferred',
  'Not Required',
];

interface EditEpicModalProps {
  epic: Epic;
  requirements: Requirement[];
  onClose: () => void;
  onSave: (updates: Partial<Epic>) => Promise<void>;
}

export default function EditEpicModal({ epic, requirements, onClose, onSave }: EditEpicModalProps) {
  const [title, setTitle] = useState(epic.title || '');
  const [idLabel, setIdLabel] = useState(epic.idLabel || '');
  const [description, setDescription] = useState(epic.description || '');
  const [status, setStatus] = useState<TaskStatus>(epic.status || 'Not Started');
  const [targetEndDate, setTargetEndDate] = useState(
    epic.targetEndDate ? epic.targetEndDate.slice(0, 10) : ''
  );
  const [showOnCalendar, setShowOnCalendar] = useState(Boolean(epic.showOnCalendar));
  const [costPerHour, setCostPerHour] = useState<string>(
    typeof epic.costPerHour === 'number' ? String(epic.costPerHour) : ''
  );
  const [requirementIds, setRequirementIds] = useState<string[]>(epic.requirementIds || []);
  const [isSaving, setIsSaving] = useState(false);

  const toggleRequirement = (reqId: string) => {
    setRequirementIds((prev) =>
      prev.includes(reqId) ? prev.filter((id) => id !== reqId) : [...prev, reqId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error('Epic title is required');
      return;
    }

    const updates: Partial<Epic> = {
      title: trimmedTitle,
      idLabel: idLabel.trim() || undefined,
      description: description,
      status,
      targetEndDate: targetEndDate || undefined,
      showOnCalendar,
      requirementIds,
      updatedAt: new Date().toISOString(),
    };

    const trimmedCost = costPerHour.trim();
    if (trimmedCost === '') {
      updates.costPerHour = undefined;
    } else {
      const parsed = Number(trimmedCost);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Cost per hour must be a non-negative number');
        return;
      }
      updates.costPerHour = parsed;
    }

    try {
      setIsSaving(true);
      await onSave(updates);
    } catch (err: any) {
      console.error('Error saving epic:', err);
      toast.error(err?.message || 'Failed to save epic');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-white px-6 py-4">
          <h2 className="text-xl font-semibold text-foreground">Edit Epic</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-muted/40"
            aria-label="Close edit epic modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div>
            <label htmlFor="edit-epic-title" className="mb-1.5 block text-sm font-medium text-foreground">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="edit-epic-title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="edit-epic-id-label" className="mb-1.5 block text-sm font-medium text-foreground">
                ID Label
              </label>
              <input
                id="edit-epic-id-label"
                type="text"
                value={idLabel}
                onChange={(e) => setIdLabel(e.target.value)}
                placeholder="e.g., AUTH"
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="edit-epic-status" className="mb-1.5 block text-sm font-medium text-foreground">
                Status
              </label>
              <select
                id="edit-epic-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="edit-epic-description" className="mb-1.5 block text-sm font-medium text-foreground">
              Description
            </label>
            <textarea
              id="edit-epic-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="What is this epic about?"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="edit-epic-target-end-date" className="mb-1.5 block text-sm font-medium text-foreground">
                Target End Date
              </label>
              <input
                id="edit-epic-target-end-date"
                type="date"
                value={targetEndDate}
                onChange={(e) => setTargetEndDate(e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="edit-epic-cost-per-hour" className="mb-1.5 block text-sm font-medium text-foreground">
                Cost per hour ($/hr)
              </label>
              <input
                id="edit-epic-cost-per-hour"
                type="number"
                min="0"
                step="0.01"
                value={costPerHour}
                onChange={(e) => setCostPerHour(e.target.value)}
                placeholder="e.g., 75"
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={showOnCalendar}
              onChange={(e) => setShowOnCalendar(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            Show this epic's deadline on calendar
          </label>

          {requirements.length > 0 && (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-foreground">
                Linked Requirements
              </span>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {requirements.map((req) => (
                  <label
                    key={req.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={requirementIds.includes(req.id)}
                      onChange={() => toggleRequirement(req.id)}
                      className="h-4 w-4 rounded border-border"
                    />
                    <span className="font-mono text-xs text-muted-foreground">{req.code}</span>
                    <span className="truncate text-foreground">{req.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
